import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, MethodologyMode, ProjectLifecycleStatus } from '../../../types';
import { updateProject, updateProjectStatus, captureProjectBaseline } from '../../../services/projectService';
import AutomationRulesSection from '../../../components/automation/AutomationRulesSection';
import DeleteProjectModal from '../../../components/DeleteProjectModal';
import BoardModal, { BMCancelButton, BMPrimaryButton, BMDangerButton, BM_LABEL } from '../../../components/BoardModal';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import ApprovalWorkflowsSection from '../ApprovalWorkflowsSection';
import ProjectLibrariesSection from '../../../components/settings/ProjectLibrariesSection';
import TagManagementSection from '../../../components/settings/TagManagementSection';
import StakeholderAccessSection from '../../../components/settings/StakeholderAccessSection';
import TeamRolesSection from '../../../components/settings/TeamRolesSection';
import CustomFieldsSection from '../../../components/settings/CustomFieldsSection';
import M365GroupSection from '../../../components/settings/M365GroupSection';
import SprintsSection from '../../../components/agile/SprintsSection';
import { ResourcePool, getTenantPool } from '../../../services/resourcePoolService';
import ProjectResourcesSection from './ProjectResourcesSection';
import ResourceCalendarsSection from './ResourceCalendarsSection';

interface Props { pca: IPublicClientApplication; project: Project; onSaved: () => void; }

interface ModuleMeta { key: keyof Project; label: string; desc: string }
const MODULE_KEYS: ModuleMeta[] = [
  { key: 'dagrun_pm_module_goals',            label: 'Goals',                 desc: 'Track measurable objectives alongside tasks' },
  { key: 'dagrun_pm_module_issues',           label: 'Issues',                desc: 'Track blockers and open problems' },
  { key: 'dagrun_pm_module_risks',            label: 'Risks',                 desc: 'Risk register with probability/impact scoring' },
  { key: 'dagrun_pm_module_milestones',       label: 'Milestones',            desc: 'Mark key deliverables + phases' },
  { key: 'dagrun_pm_module_timetracking',     label: 'Time Tracking',         desc: 'Log hours per task; effort + billable reporting' },
  { key: 'dagrun_pm_module_budget',           label: 'Budget & Expenses',     desc: 'Budget lines and expense submissions' },
  { key: 'dagrun_pm_module_resources',        label: 'Resources',             desc: 'Team capacity + workload view' },
  { key: 'dagrun_pm_module_changemanagement', label: 'Change Management',     desc: 'Formal change requests + approvals' },
  { key: 'dagrun_pm_module_meetingintel',     label: 'Meeting Intelligence',  desc: 'Capture action items from Teams meetings' },
  { key: 'dagrun_pm_module_clientportal',     label: 'Client Portal',         desc: 'Share project status with external clients' },
  { key: 'dagrun_pm_module_ai',               label: 'AI Agent',              desc: 'AI-assisted scheduling and suggestions' },
  { key: 'dagrun_pm_module_agile',            label: 'Agile',                 desc: 'Sprints, backlog, burndown' },
  { key: 'dagrun_pm_module_training',         label: 'Training Records',      desc: 'Required + completed training per team member' },
  { key: 'dagrun_pm_module_punchlist',        label: 'Punch List',            desc: 'Construction punch items with inspection workflow' },
  { key: 'dagrun_pm_module_stakeholders',     label: 'Stakeholder Register',  desc: 'Influence/interest matrix + engagement log' },
  { key: 'dagrun_pm_module_submittalrfi',     label: 'Submittal & RFI Tracking', desc: 'Submittal log + RFI register with status workflow and approvals' },
  { key: 'dagrun_pm_module_lessons',          label: 'Lessons Learned',      desc: 'Post-mortem log of issues, impacts, recommendations' },
];

export default function SettingsTab({ pca, project, onSaved }: Props) {
  const [draft, setDraft] = useState<Project>(project);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo, setTransferTo] = useState<Person | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [baselineLabel, setBaselineLabel] = useState('');
  const [showCaptureBaseline, setShowCaptureBaseline] = useState(false);
  const [capturingBaseline, setCapturingBaseline] = useState(false);
  const [baselineMsg, setBaselineMsg] = useState<string | null>(null);
  // undefined = loading; null = tenant has no pool
  const [tenantPool, setTenantPool] = useState<ResourcePool | null | undefined>(undefined);
  const [showLinkWarning, setShowLinkWarning] = useState(false);
  const [poolBusy, setPoolBusy] = useState(false);
  const navigate = useNavigate();
  const me = useCurrentUser(pca, project.dagrun_pm_projectid);

  useEffect(() => {
    getTenantPool(pca).then(setTenantPool).catch(() => setTenantPool(null));
  }, [pca, project.dagrun_pm_projectid]);

  const set = <K extends keyof Project>(k: K, v: Project[K]) => setDraft({ ...draft, [k]: v });

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const patch: Record<string, unknown> = {
        dagrun_pm_name: draft.dagrun_pm_name,
        dagrun_pm_methodologymode: draft.dagrun_pm_methodologymode,
        dagrun_pm_currency: draft.dagrun_pm_currency,
        dagrun_pm_isbillable: draft.dagrun_pm_isbillable,
      };
      MODULE_KEYS.forEach(m => { patch[m.key as string] = draft[m.key]; });
      await updateProject(pca, project.dagrun_pm_projectid, patch);
      setMsg('Saved');
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };


  const handleArchive = async () => {
    setArchiving(true);
    try {
      await updateProjectStatus(pca, project.dagrun_pm_projectid, ProjectLifecycleStatus.Archived);
      setShowArchive(false);
      navigate('/portfolio');
    } catch {
      setArchiving(false);
    }
  };

  const handleCaptureBaseline = async () => {
    const label = baselineLabel.trim();
    if (!label) return;
    setCapturingBaseline(true);
    setBaselineMsg(null);
    try {
      const { tasksSnapshotted } = await captureProjectBaseline(pca, project.dagrun_pm_projectid, label);
      setBaselineMsg(`Baseline "${label}" captured (${tasksSnapshotted} task${tasksSnapshotted === 1 ? '' : 's'} snapshotted).`);
      setShowCaptureBaseline(false);
      setBaselineLabel('');
      onSaved();
    } catch (e) {
      setBaselineMsg(e instanceof Error ? e.message : 'Capture failed');
    } finally {
      setCapturingBaseline(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    setTransferring(true);
    setTransferError(null);
    try {
      await updateProject(pca, project.dagrun_pm_projectid, {
        dagrun_pm_ownerid: transferTo.aadObjectId,
        dagrun_pm_ownername: transferTo.displayName,
      });
      setShowTransfer(false);
      setTransferTo(null);
      onSaved();
    } catch (e) {
      setTransferError(e instanceof Error ? e.message : 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 760 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3>Settings</h3>
      </div>

      {/* General */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>GENERAL</div>
        <label>Project Name</label>
        <input value={draft.dagrun_pm_name} onChange={e => set('dagrun_pm_name', e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label>Methodology</label>
            <select value={draft.dagrun_pm_methodologymode} onChange={e => set('dagrun_pm_methodologymode', Number(e.target.value))}>
              <option value={MethodologyMode.Waterfall}>Waterfall</option>
              <option value={MethodologyMode.Agile}>Agile</option>
              <option value={MethodologyMode.Kanban}>Kanban</option>
              <option value={MethodologyMode.Hybrid}>Hybrid</option>
            </select>
          </div>
          <div>
            <label>Currency (ISO)</label>
            <input maxLength={3} value={draft.dagrun_pm_currency ?? ''} onChange={e => set('dagrun_pm_currency', e.target.value || null)} />
          </div>
        </div>
        <label style={{ marginTop: 12 }}>
          <input type="checkbox" checked={!!draft.dagrun_pm_isbillable} onChange={e => set('dagrun_pm_isbillable', e.target.checked)} />
          {' '}Is Billable
        </label>
      </div>

      {/* Team & Roles — all members + roles, PM(s) badged. Visible to everyone;
          only PMs can manage (guarded so a project can't lose its last PM). */}
      <TeamRolesSection pca={pca} projectId={project.dagrun_pm_projectid} groupId={project.dagrun_pm_m365groupid ?? null} canManage={!!me?.isPM} />

      {/* Modules — toggles that PATCH immediately. PM-only; other roles see read-only. */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>MODULES</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
          Toggle modules on to show their tab in the project nav bar.
          Toggling off hides the tab — existing module data is preserved and
          reappears the moment the module is re-enabled.
          {!me?.isPM && ' Read-only for non-PM team members.'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {MODULE_KEYS.map(m => {
            const on = !!draft[m.key];
            const disabled = !me?.isPM;
            return (
              <div key={m.key as string}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: 10,
                  border: '1px solid var(--border-light)',
                  borderRadius: 6,
                  background: on ? 'rgba(14,116,144,0.04)' : '#fff',
                  opacity: disabled ? 0.6 : 1,
                }}>
                <ModuleToggle
                  checked={on}
                  disabled={disabled}
                  onChange={(next) => {
                    set(m.key, next as Project[typeof m.key]);
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>{m.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        {msg && <span style={{ color: msg === 'Saved' ? 'var(--success)' : 'var(--danger)', fontSize: 13 }}>{msg}</span>}
      </div>

      {/* Document Libraries — linked SharePoint libraries for this project. */}
      <div style={{ marginTop: 24 }}>
        <ProjectLibrariesSection pca={pca} projectId={project.dagrun_pm_projectid} />
      </div>

      {/* Sprints — only when the Agile module is enabled. */}
      {draft.dagrun_pm_module_agile && (
        <div style={{ marginTop: 24 }}>
          <SprintsSection pca={pca} projectId={project.dagrun_pm_projectid} />
        </div>
      )}

      {/* Automation — full IF/THEN rule builder */}
      <AutomationRulesSection pca={pca} projectId={project.dagrun_pm_projectid} />

      {/* Baseline */}
      {me?.isPM && (
        <div className="card" style={{ padding: 20, marginTop: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>BASELINE</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 14 }}>
            Snapshot the current task start/due dates as a baseline. The Gantt
            view will then show baseline ghost bars so schedule slippage is
            visible. Capturing replaces any existing baseline.
          </div>
          {project.dagrun_pm_baseline_capturedon && (
            <div style={{
              padding: '8px 10px', marginBottom: 14,
              background: 'var(--surface-2)', borderRadius: 6,
              borderLeft: '3px solid var(--brand)',
              fontSize: 12, color: 'var(--ink-2)',
            }}>
              Current baseline: <strong>{project.dagrun_pm_baseline_label ?? '(unlabeled)'}</strong>
              <span style={{ color: 'var(--ink-4)', marginLeft: 8 }}>
                captured {new Date(project.dagrun_pm_baseline_capturedon).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>
                Baseline Label
              </label>
              <input
                type="text"
                value={baselineLabel}
                onChange={e => setBaselineLabel(e.target.value)}
                placeholder="e.g. Phase 1 Kickoff, Post-scoping v1"
                style={{ width: '100%' }}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowCaptureBaseline(true)}
              disabled={!baselineLabel.trim()}
              style={{
                background: baselineLabel.trim() ? '#0E7490' : '#B8BEC9',
                color: '#fff', border: 'none', borderRadius: 6,
                padding: '8px 14px',
                fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600,
                cursor: baselineLabel.trim() ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
              }}
            >Capture Baseline</button>
          </div>
          {baselineMsg && (
            <div style={{
              marginTop: 10, padding: '8px 10px',
              background: baselineMsg.startsWith('Baseline') ? '#D1FAE5' : '#FEE2E2',
              borderLeft: `3px solid ${baselineMsg.startsWith('Baseline') ? '#059669' : '#DC2626'}`,
              color: baselineMsg.startsWith('Baseline') ? '#065F46' : '#991B1B',
              fontSize: 12, borderRadius: 4,
            }}>{baselineMsg}</div>
          )}
        </div>
      )}

      {/* Resources — global resource pool linkage (one pool per tenant) */}
      {me?.isPM && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>RESOURCES</div>
          {(() => {
            const linked = !!draft.dagrun_pm_useglobalresourcepool && !!draft.dagrun_pm_resourcepoolid;
            const unlink = async () => {
              if (poolBusy) return;
              setPoolBusy(true);
              try {
                await updateProject(pca, project.dagrun_pm_projectid, {
                  dagrun_pm_useglobalresourcepool: false,
                  dagrun_pm_resourcepoolid: null,
                });
                setDraft(d => ({ ...d, dagrun_pm_useglobalresourcepool: false, dagrun_pm_resourcepoolid: null }));
                onSaved();
              } catch { /* re-read on next refresh */ }
              finally { setPoolBusy(false); }
            };
            if (linked) {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <span className="pill green">Linked</span>
                    <span style={{ fontSize: 13, color: 'var(--ink-2)', marginLeft: 10 }}>
                      This project draws from <strong>{tenantPool?.dagrun_pm_name ?? 'the global resource pool'}</strong>.
                      Pool default rates apply to unrated resources; per-project overrides win.
                    </span>
                  </div>
                  <button className="btn-secondary" onClick={unlink} disabled={poolBusy}>
                    {poolBusy ? 'Working…' : 'Unlink'}
                  </button>
                </div>
              );
            }
            if (tenantPool === undefined) {
              return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Checking tenant resource pool…</div>;
            }
            if (tenantPool === null) {
              return (
                <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  Not linked. No resource pool exists for this tenant yet — configure one in{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    style={{ background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', padding: 0 }}
                  >Tenant Admin</button>, then link this project here.
                </div>
              );
            }
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                  <span className="pill neutral">Not linked</span>
                  <span style={{ marginLeft: 10 }}>
                    Link to <strong>{tenantPool.dagrun_pm_name}</strong> to make pool members and
                    their default rates available to this project.
                  </span>
                </div>
                <button className="btn-primary" onClick={() => setShowLinkWarning(true)} disabled={poolBusy}>
                  Link to Resource Pool
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Project resources — group↔pool auto-sync roster (only when pool-linked) */}
      {me?.isPM && !!draft.dagrun_pm_useglobalresourcepool && !!draft.dagrun_pm_resourcepoolid && (
        <ProjectResourcesSection
          pca={pca}
          projectId={project.dagrun_pm_projectid}
          poolId={draft.dagrun_pm_resourcepoolid}
          groupId={project.dagrun_pm_m365groupid}
        />
      )}

      {showLinkWarning && tenantPool && (
        <BoardModal open title="Link to Resource Pool" onClose={() => !poolBusy && setShowLinkWarning(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowLinkWarning(false)} disabled={poolBusy} />
            <BMPrimaryButton
              disabled={poolBusy}
              onClick={async () => {
                if (poolBusy) return;
                setPoolBusy(true);
                try {
                  await updateProject(pca, project.dagrun_pm_projectid, {
                    dagrun_pm_useglobalresourcepool: true,
                    dagrun_pm_resourcepoolid: tenantPool.dagrun_pm_resourcepoolid,
                  });
                  setDraft(d => ({
                    ...d,
                    dagrun_pm_useglobalresourcepool: true,
                    dagrun_pm_resourcepoolid: tenantPool.dagrun_pm_resourcepoolid,
                  }));
                  setShowLinkWarning(false);
                  onSaved();
                } catch { /* modal stays open; user can retry */ }
                finally { setPoolBusy(false); }
              }}
            >{poolBusy ? 'Linking…' : 'Link Project'}</BMPrimaryButton>
          </>}>
          Linking to the resource pool will apply pool default rates to unrated resources
          in this project. Existing rate overrides are preserved.
        </BoardModal>
      )}

      {/* Custom Fields — project-defined task fields. PM-only. */}
      {me?.isPM && (
        <CustomFieldsSection pca={pca} projectId={project.dagrun_pm_projectid} />
      )}

      {/* Resource Calendars — per-worker work-calendar assignments. PM-only. */}
      {me?.isPM && (
        <ResourceCalendarsSection pca={pca} projectId={project.dagrun_pm_projectid} />
      )}

      {/* Tag Management — rename-in-place for task + decision tags. PM-only. */}
      {me?.isPM && (
        <TagManagementSection pca={pca} projectId={project.dagrun_pm_projectid} />
      )}

      {/* Stakeholder Access — per-surface visibility toggles. PM-only. */}
      {me?.isPM && (
        <StakeholderAccessSection pca={pca} projectId={project.dagrun_pm_projectid} />
      )}

      {/* Approval Workflows */}
      {me?.isPM && (
        <ApprovalWorkflowsSection pca={pca} projectId={project.dagrun_pm_projectid} />
      )}

      {/* Microsoft 365 Group linkage — above the Danger Zone. */}
      <M365GroupSection pca={pca} project={project} onChanged={onSaved} />

      {/* Advanced */}
      <div className="card" style={{
        padding: 20, marginTop: 24,
      }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>ADVANCED</div>

        {me?.isPM && (
          <>
            <ActionRow
              title="Transfer ownership"
              subtitle="Assign a new owner for this project. Existing members remain on the project."
              buttonLabel="Transfer"
              buttonColor="#0E7490"
              onClick={() => setShowTransfer(true)}
            />
            <ActionRow
              title="Archive this project"
              subtitle="Set the lifecycle status to Archived. Data is preserved and you can re-activate later."
              buttonLabel="Archive"
              buttonColor="#92400E"
              onClick={() => setShowArchive(true)}
            />
          </>
        )}
        <ActionRow
          title="Delete this project"
          subtitle="Permanently remove this project and all of its data. Requires you to type the project name to confirm."
          buttonLabel="Delete Project"
          buttonColor="#DC2626"
          onClick={() => setShowDelete(true)}
          last
        />
      </div>

      {showDelete && (
        <DeleteProjectModal
          pca={pca}
          projectId={project.dagrun_pm_projectid}
          projectName={project.dagrun_pm_name}
          onDeleted={() => { setShowDelete(false); navigate('/portfolio'); window.alert('Project deleted.'); }}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {showArchive && (
        <BoardModal open title="Archive project" onClose={() => !archiving && setShowArchive(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowArchive(false)} disabled={archiving} />
            <BMDangerButton onClick={handleArchive} disabled={archiving}>
              {archiving ? 'Archiving…' : 'Archive Project'}
            </BMDangerButton>
          </>}>
          Archive <strong style={{ color: '#0F1117' }}>{project.dagrun_pm_name}</strong>? The project will be removed
          from active lists but all data is preserved. You can re-activate it from the portfolio filter later.
        </BoardModal>
      )}

      {showCaptureBaseline && (
        <BoardModal open title="Capture baseline" onClose={() => !capturingBaseline && setShowCaptureBaseline(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowCaptureBaseline(false)} disabled={capturingBaseline} />
            <BMPrimaryButton onClick={handleCaptureBaseline} disabled={capturingBaseline || !baselineLabel.trim()}>
              {capturingBaseline ? 'Capturing…' : 'Capture Baseline'}
            </BMPrimaryButton>
          </>}>
          <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5 }}>
            {project.dagrun_pm_baseline_capturedon
              ? 'This will OVERWRITE the existing baseline. Continue?'
              : 'This will snapshot the current start/due dates across all tasks with dates.'}
            <div style={{ marginTop: 10 }}>
              New label: <strong style={{ color: '#0F1117' }}>{baselineLabel || '(empty)'}</strong>
            </div>
          </div>
        </BoardModal>
      )}

      {showTransfer && (
        <BoardModal open title="Transfer ownership" onClose={() => !transferring && setShowTransfer(false)}
          footer={<>
            <BMCancelButton onClick={() => setShowTransfer(false)} disabled={transferring} />
            <BMPrimaryButton onClick={handleTransfer} disabled={transferring || !transferTo}>
              {transferring ? 'Transferring…' : 'Transfer'}
            </BMPrimaryButton>
          </>}>
          <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5, marginBottom: 14 }}>
            Current owner: <strong style={{ color: '#0F1117' }}>
              {project.dagrun_pm_ownername ?? (project.dagrun_pm_ownerid ? 'Unknown user' : 'Unassigned')}
            </strong>.
            Select the new owner below.
          </div>
          <label style={BM_LABEL}>New Owner</label>
          <PeoplePicker pca={pca} value={transferTo} onChange={setTransferTo} placeholder="Select new owner…" />
          {transferError && <div style={{ marginTop: 10, color: '#DC2626', fontSize: 12 }}>{transferError}</div>}
        </BoardModal>
      )}
    </div>
  );
}

// Toggle switch matching the design system. Pure presentation — the
// consumer owns the data and the fire-and-forget persistence.
function ModuleToggle({ checked, disabled, onChange }: { checked: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      aria-pressed={checked}
      style={{
        position: 'relative', flexShrink: 0,
        width: 36, height: 20,
        border: 'none',
        borderRadius: 10,
        background: checked ? '#0E7490' : '#DDE0E6',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        transition: 'background 0.15s',
        marginTop: 2,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 2, left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        transition: 'left 0.15s',
      }} />
    </button>
  );
}

function ActionRow({ title, subtitle, buttonLabel, buttonColor, onClick, last }: {
  title: string; subtitle: string; buttonLabel: string; buttonColor: string;
  onClick: () => void; last?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      paddingBottom: last ? 0 : 12, marginBottom: last ? 0 : 12,
      borderBottom: last ? 'none' : '1px solid var(--border-light)',
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>{subtitle}</div>
      </div>
      <button
        type="button"
        onClick={onClick}
        style={{
          background: '#fff', color: buttonColor,
          border: `1px solid ${buttonColor}`, borderRadius: 6,
          padding: '7px 14px',
          fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'background 0.12s, color 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = buttonColor; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = buttonColor; }}
      >{buttonLabel}</button>
    </div>
  );
}
